import { Divider, Grid, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AccordionType,
  ButtonFieldType,
  CheckboxFieldType,
  DatePickerFieldType,
  DateWithInfinityFieldType,
  DividerType,
  FileFieldType,
  FormField,
  FormFieldType,
  IconButtonFieldType,
  IconSelectFieldType,
  LinkedGroupedMultiSelectFieldType,
  LinkedSelectFieldType,
  LocalTimeFieldType,
  MultiSelectFieldType,
  SelectFieldType,
  TextFieldType,
  TypographyType,
} from '@/type';
import Button from '../button/Button';
import CheckboxController from '@components/common/dynamicFormFields/controllers/CheckboxController';
import DateWithInfinityController from '@components/common/dynamicFormFields/controllers/DateWithInfinityController';
import IconButtonFieldController from '@components/common/dynamicFormFields/controllers/IconButtonController';
import LinkedSelectController from '@components/common/dynamicFormFields/controllers/LinkedSelectController';
import SelectController from '@components/common/dynamicFormFields/controllers/SelectController';
import TextFieldController from '@components/common/dynamicFormFields/controllers/TextFieldController';
import DatePickerFieldController from '@components/common/dynamicFormFields/controllers/DatePickerFieldController';
import LocalTimeController from '@components/common/dynamicFormFields/controllers/LocalTimeController';
import DateTimePickerFieldController from '@components/common/dynamicFormFields/controllers/DateTimePickerFieldController';
import PasswordFieldController from '@components/common/dynamicFormFields/controllers/PasswordFieldController';
import Accordion from '@components/common/accordion/Accordion';
import FileFieldController from '@components/common/dynamicFormFields/controllers/FileFieldController';
import MultiSelectController from '@components/common/dynamicFormFields/controllers/MultiSelectController';
import LinkedGroupedMultiSelectController from '@components/common/dynamicFormFields/controllers/LinkedGroupedMultiSelectController';
import IconSelectController from '@components/common/dynamicFormFields/controllers/IconSelectController';
import { DynamicFormLoadingSkeleton } from '@/components';

interface DynamicFormFieldsProps<TFieldValues extends FieldValues = FieldValues> {
  formFields: FormField[];
  control: Control<TFieldValues>;
  disabled?: boolean;
  isLoading?: boolean;
}

const DynamicFormFields = <TFieldValues extends FieldValues = FieldValues>({
  formFields,
  control,
  disabled = false,
  isLoading,
}: DynamicFormFieldsProps<TFieldValues>) => {
  const { t } = useTranslation();

  if (isLoading) return <DynamicFormLoadingSkeleton formFields={formFields} />;

  const getDisabled = (isFormFieldDisabled: boolean) => {
    if (isFormFieldDisabled) return true;
    return disabled;
  };

  const renderTextField = ({
    xs = 12,
    required = false,
    defaultValue = '',
    inputProps = {},
    isMultiRow = false,
    stepper = false,
    ...otherProps
  }: TextFieldType) => (
    <Grid item key={otherProps.name} xs={xs}>
      <Tooltip title={t(otherProps.tooltip)}>
        <div>
          <TextFieldController
            control={control}
            textField={{
              required,
              defaultValue,
              disabled: getDisabled(otherProps.disabled),
              inputProps,
              isMultiRow,
              stepper,
              ...otherProps,
            }}
          />
        </div>
      </Tooltip>
    </Grid>
  );

  const renderTypographyField = ({
    variant,
    text,
    xs = 12,
    isTranslate = true,
  }: TypographyType) => (
    <Grid item key={text} xs={xs}>
      <Typography variant={variant} gutterBottom align='left'>
        {isTranslate ? t(text) : text}
      </Typography>
    </Grid>
  );

  const renderButtonField = ({
    label,
    onClick,
    xs = 12,
    isTranslate = true,
  }: ButtonFieldType) => (
    <Grid item key={label} xs={xs}>
      <Button disabled={disabled} onClick={onClick}>
        {isTranslate ? t(label) : label}
      </Button>
    </Grid>
  );

  const renderIconButtonField = ({ xs = 12, ...other }: IconButtonFieldType) => (
    <Grid item xs={xs}>
      <IconButtonFieldController iconButtonField={{ ...other, disabled }} />
    </Grid>
  );

  const renderSelectField = ({
    required = false,
    isMultiselect = false,
    xs = 12,
    ...otherProps
  }: SelectFieldType) => (
    <Grid item key={otherProps.name} xs={xs}>
      <Tooltip title={t(otherProps.tooltip)}>
        <div>
          <SelectController<TFieldValues>
            control={control}
            selectField={{
              ...otherProps,
              required,
              type: FormFieldType.Select,
              isMultiselect,
              disabled: getDisabled(otherProps.disabled),
            }}
          />
        </div>
      </Tooltip>
    </Grid>
  );

  const renderIconSelectField = ({
    required = false,
    isMultiselect = false,
    xs = 12,
    ...otherProps
  }: IconSelectFieldType) => (
    <Grid item key={otherProps.name} xs={xs}>
      <Tooltip title={t(otherProps.tooltip)}>
        <div>
          <IconSelectController<TFieldValues>
            control={control}
            selectField={{
              ...otherProps,
              required,
              type: FormFieldType.IconSelect,
              isMultiselect,
              disabled: getDisabled(otherProps.disabled),
            }}
          />
        </div>
      </Tooltip>
    </Grid>
  );

  const renderCheckboxField = ({ xs = 12, ...otherProps }: CheckboxFieldType) => {
    return (
      <Grid item key={otherProps.name} xs={xs}>
        <Tooltip title={t(otherProps.tooltip)}>
          <div>
            <CheckboxController<TFieldValues>
              control={control}
              checkboxField={{
                disabled: getDisabled(otherProps.disabled),
                ...otherProps,
              }}
            />
          </div>
        </Tooltip>
      </Grid>
    );
  };

  const renderLinkedSelectFields = ({
    xs = 12,
    ...otherProps
  }: LinkedSelectFieldType) => (
    <LinkedSelectController<TFieldValues>
      control={control}
      disabled={getDisabled(otherProps.disabled)}
      linkedSelectField={otherProps}
    />
  );

  const renderDateWithInfinityFields = ({
    xs = 12,
    ...otherProps
  }: DateWithInfinityFieldType) => (
    <Grid item xs={xs}>
      <Tooltip title={t(otherProps.tooltip)}>
        <div>
          <DateWithInfinityController<TFieldValues>
            control={control}
            disabled={getDisabled(otherProps.disabled)}
            dateWithInfinityField={otherProps}
          />
        </div>
      </Tooltip>
    </Grid>
  );

  const renderLocalTimeField = ({ xs = 12, ...otherProps }: LocalTimeFieldType) => (
    <Grid item xs={xs}>
      <Tooltip title={t(otherProps.tooltip)}>
        <div>
          <LocalTimeController<TFieldValues>
            control={control}
            disabled={getDisabled(otherProps.disabled)}
            localTimeFieldType={otherProps}
          />
        </div>
      </Tooltip>
    </Grid>
  );

  const renderDatePickerFields = ({ xs = 12, ...otherProps }: DatePickerFieldType) => (
    <Grid item xs={xs}>
      <Tooltip title={t(otherProps.tooltip)}>
        <div>
          <DatePickerFieldController
            control={control}
            textField={otherProps}
            disabled={getDisabled(otherProps.disabled)}
          />
        </div>
      </Tooltip>
    </Grid>
  );

  const renderDateTimePickerFields = ({ xs = 12, ...otherProps }: TextFieldType) => (
    <Grid item xs={xs}>
      <Tooltip title={t(otherProps.tooltip)}>
        <div>
          <DateTimePickerFieldController
            control={control}
            textField={otherProps}
            disabled={getDisabled(otherProps.disabled)}
          />
        </div>
      </Tooltip>
    </Grid>
  );

  const renderPasswordField = ({
    xs = 12,
    required = false,
    ...otherProps
  }: TextFieldType) => (
    <Grid item xs={xs} key={otherProps.name}>
      <Tooltip title={t(otherProps.tooltip)}>
        <div>
          <PasswordFieldController control={control} textField={{ ...otherProps }} />
        </div>
      </Tooltip>
    </Grid>
  );

  const renderNumberFields = ({
    xs = 12,
    mask = 'number',
    ...otherProps
  }: TextFieldType) => renderTextField({ xs, mask, ...otherProps });

  const renderAccordionField = ({
    xs = 12,
    label,
    formFields,
    expanded = false,
    variant = 'h4',
  }: AccordionType) => (
    <Grid item xs={xs} key={label}>
      <Accordion expanded={expanded} label={label} variant={variant}>
        {formFields?.map(renderField)}
      </Accordion>
    </Grid>
  );

  const renderFileField = ({ xs = 12, ...otherProps }: FileFieldType) => (
    <Grid item xs={xs} key={otherProps.name}>
      <FileFieldController<TFieldValues>
        control={control}
        fileField={{ ...otherProps }}
      />
    </Grid>
  );

  const renderMultiSelectField = ({ xs = 12, ...otherProps }: MultiSelectFieldType) => (
    <Grid item key={otherProps.name} xs={xs}>
      <MultiSelectController
        control={control}
        multiSelectField={{
          ...otherProps,
          disabled: getDisabled(otherProps.disabled),
        }}
      />
    </Grid>
  );

  const renderGenericMultiSelectField = ({
    xs = 12,
    ...otherProps
  }: LinkedGroupedMultiSelectFieldType) => (
    <Grid item key={otherProps.itemLabel} xs={xs}>
      <LinkedGroupedMultiSelectController<TFieldValues>
        control={control}
        {...otherProps}
      />
    </Grid>
  );

  const renderField = (field: FormField): React.ReactNode => {
    switch (field.type) {
      case FormFieldType.Select:
        return renderSelectField(field as SelectFieldType);
      case FormFieldType.IconSelect:
        return renderIconSelectField(field as IconSelectFieldType);
      case FormFieldType.Checkbox:
        return renderCheckboxField(field as CheckboxFieldType);
      case FormFieldType.Typography:
        return renderTypographyField(field as TypographyType);
      case FormFieldType.Button:
        return renderButtonField(field as ButtonFieldType);
      case FormFieldType.IconButton:
        return renderIconButtonField(field as IconButtonFieldType);
      case FormFieldType.LinkedSelect:
        return renderLinkedSelectFields(field as LinkedSelectFieldType);
      case FormFieldType.DateWithInfinity:
        return renderDateWithInfinityFields(field as DateWithInfinityFieldType);
      case FormFieldType.Divider:
        return (
          <Grid item xs={12}>
            <Divider style={(field as DividerType)?.style} />
          </Grid>
        );
      case FormFieldType.Date:
        return renderDatePickerFields(field as DatePickerFieldType);
      case FormFieldType.FloatNumber:
      case FormFieldType.Number:
        return renderNumberFields(field as TextFieldType);
      case FormFieldType.Text:
        return renderTextField(field as TextFieldType);
      case FormFieldType.Password:
        return renderPasswordField(field as TextFieldType);
      case FormFieldType.LocalTime:
        return renderLocalTimeField(field as LocalTimeFieldType);
      case FormFieldType.DateTime:
        return renderDateTimePickerFields(field as TextFieldType);
      case FormFieldType.Accordion:
        return renderAccordionField(field as AccordionType);
      case FormFieldType.File:
        return renderFileField(field as FileFieldType);
      case FormFieldType.MultiSelect:
        return renderMultiSelectField(field as MultiSelectFieldType);
      case FormFieldType.LinkedGroupedMultiSelect:
        return renderGenericMultiSelectField(field as LinkedGroupedMultiSelectFieldType);
    }
  };

  return <>{formFields.map(renderField)}</>;
};

export default DynamicFormFields;
